import * as React from 'react'
import {useLocation} from 'react-router'
import map from 'lodash/map'
import filter from 'lodash/filter'
import isNaN from 'lodash/isNaN'
import negate from 'lodash/negate'
import {mkNonEmptyFromJust} from '@freckle/non-empty'
import {fromJust, fromMaybe} from '@freckle/maybe'
import {extractQueryParams} from '@frontrow/console/src/entities/helpers/routers/query-params'

import {
  type SchoolBatchUpdateResponseT,
  type BulkUpdateSchoolConfigurationDataT
} from '@frontrow/console/src/models/school-batch-update'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import SpinnerWrapper from '@frontrow/console/src/components/common/spinner-wrapper'
import {BulkEditSchoolConfigurationContainer} from '@frontrow/console/src/components/pages/bulk-edit-school-configuration/container'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import {parseFiltersFromQueryParams} from '@frontrow/console/src/helpers/data-table'
import {useSchools, useBatchUpdateSchoolConfiguration} from '@frontrow/console/src/hooks/use-school'

export function BulkEditSchoolConfiguration(): React.ReactElement {
  const location = useLocation()

  // Parse URL parameters
  const params = fromMaybe(() => new Map(), extractQueryParams(location))
  const paramSchoolIds = fromJust(params.get('schoolIds'), `schoolIds param is part of the URL`)
  const clonedParams = new Map(params)
  clonedParams.delete('schoolIds')
  const filters = parseFiltersFromQueryParams(clonedParams)
  const selectedSchoolIds = mkNonEmptyFromJust(
    filter(
      map(paramSchoolIds.split(','), id => parseInt(id, 10)),
      negate(isNaN)
    )
  )

  // Fetch schools data using hooks
  const fetchDataParams: FetchDataParamsT = {filters}
  const {schools, isLoading, error} = useSchools(fetchDataParams)
  const {batchUpdate, isUpdating, error: batchError} = useBatchUpdateSchoolConfiguration()
  const [batchUpdateResponse, setBatchUpdateResponse] =
    React.useState<SchoolBatchUpdateResponseT | null>(null)

  // Handle batch update
  const handleBatchUpdate = async (updatedSchools: Array<BulkUpdateSchoolConfigurationDataT>) => {
    try {
      const response = await batchUpdate(updatedSchools)
      setBatchUpdateResponse(response)
    } catch (err: unknown) {
      console.error('Batch update failed:', err)
      setBatchUpdateResponse(null)
    }
  }

  // Filter schools based on selected IDs
  const schoolData = schools ?? []
  const filteredSchoolData = filter(schoolData, school => selectedSchoolIds.includes(school.id))

  const displayError = error ?? batchError

  return (
    <SpinnerWrapper isLoading={isLoading}>
      {displayError !== null && displayError !== undefined ? (
        <ErrorAlert>{displayError}</ErrorAlert>
      ) : filteredSchoolData.length === 0 ? null : (
        <BulkEditSchoolConfigurationContainer
          schoolData={mkNonEmptyFromJust(filteredSchoolData)}
          batchUpdateSchoolConfiguration={handleBatchUpdate}
          batchUpdateInProgress={isUpdating}
          batchUpdateResponse={batchUpdateResponse}
        />
      )}
    </SpinnerWrapper>
  )
}
